import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import Loader from "../component/Loader";
import { useHandleError } from "../hooks/useHandleError";
import MessageToster from "../component/MessageToster";
import { BASE_URL } from "../api";
import { getApiHeaders, formatDate } from "../utils/helpers";
import { closeModal } from "../utils/modal";
import CommonTable from "../component/CommonTable";
import useIbStatus from "../hooks/useIbStatus";

export default function Overview() {
  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);
  const isFetchingRef = useRef(false);

  const [clientData, setClientData] = useState(() => {
    try {
      const cached = sessionStorage.getItem("ib_clients_summary");
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    ibName: '',
    yearOfExp: '',
    existingClients: '',
    avgVolume: '',
  });

  const [validationErrors, setValidationErrors] = useState({
    ibName: '',
    yearOfExp: '',
    existingClients: '',
    avgVolume: '',
  });

  const validationRules = {
    ibName: "Name is required.",
    yearOfExp: "Year of Exprience is required.",
    existingClients: "No of client is required.",
    avgVolume: "Average Volume is required.",
  };

  const {
    ibLoading,
    isActive,
    isPending,
    isNotApplied,
    refreshIbStatus
  } = useIbStatus();

  const isPageLoading = ibLoading || loading;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setValidationErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const validateForm = () => {
    const errors = {};

    Object.keys(validationRules).forEach((key) => {
      if (!formData[key] || formData[key].trim() === '') {
        errors[key] = validationRules[key];
      }
    });

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleReset = () => {
    setFormData({
      ibName: '',
      yearOfExp: '',
      existingClients: '',
      avgVolume: '',
    });
    setValidationErrors({
      ibName: '',
      yearOfExp: '',
      existingClients: '',
      avgVolume: '',
    });
  };

  const getClientAccountData = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/IB/get_ib_clients_data`, {
        headers: getApiHeaders(),
      });

      let resData = res?.data;
      if (typeof resData === "string") {
        try {
          resData = JSON.parse(resData);
        } catch {
          // ignore
        }
      }

      if (
        resData?.code === 200 ||
        resData?.code === "200" ||
        resData?.status === "success" ||
        resData?.msgcode === "Success"
      ) {
        let raw = resData?.data;
        if (typeof raw === "string") {
          try {
            raw = JSON.parse(raw);
          } catch {
            // ignore
          }
        }

        let item = {};
        if (Array.isArray(raw)) {
          item = raw[0] || {};
        } else if (raw && typeof raw === "object") {
          item = raw;
        }

        // Support user_data array safely
        let userDataList = [];
        if (Array.isArray(item.user_data)) {
          userDataList = item.user_data;
        } else if (Array.isArray(raw) && raw.length > 1 && !("total_referral" in item)) {
          userDataList = raw;
        }

        const mergedData = {
          ...item,
          total_referral: item.total_referral !== undefined && item.total_referral !== null ? item.total_referral : (item.total_referrals ?? 0),
          total_ib_accounts: item.total_ib_accounts !== undefined && item.total_ib_accounts !== null ? item.total_ib_accounts : 0,
          total_client_created_accounts: item.total_client_created_accounts !== undefined && item.total_client_created_accounts !== null ? item.total_client_created_accounts : 0,
          total_client_accounts: item.total_client_accounts !== undefined && item.total_client_accounts !== null ? item.total_client_accounts : 0,
          user_data: userDataList,
        };

        setClientData(mergedData);
        try {
          sessionStorage.setItem("ib_clients_summary", JSON.stringify(mergedData));
        } catch {
          // ignore
        }
      } else {
        MessageToster("error", resData?.message || "Something went wrong");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [handleError]);

  useEffect(() => {
    getClientAccountData();
  }, [getClientAccountData]);

  useEffect(() => {
    if (isActive) {
      getClientAccountData();
    }
  }, [isActive, getClientAccountData]);

  // Local calendar day as YYYY-MM-DD, so it compares directly with <input type="date"> values
  // and matches the day shown in the Date column.
  const toDateKey = (value) => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  };

  const today = toDateKey(new Date());
  const hasFilters = Boolean(fromDate || toDate || searchTerm.trim());

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchTerm("");
  };

  const filteredClients = useMemo(() => {
    const list = Array.isArray(clientData?.user_data) ? clientData.user_data : [];
    const term = searchTerm.trim().toLowerCase();

    return list.filter((item) => {
      if (fromDate || toDate) {
        const dateKey = toDateKey(item.create_date);
        if (!dateKey) return false;
        if (fromDate && dateKey < fromDate) return false;
        if (toDate && dateKey > toDate) return false;
      }

      if (term) {
        return [
          item.account_number,
          item.client_id,
          item.trading_platform,
          item.account_type,
          item.currency,
          item.account_status,
        ].some((val) => val != null && String(val).toLowerCase().includes(term));
      }

      return true;
    });
  }, [clientData, fromDate, toDate, searchTerm]);

  const createIBAccount = async () => {
    setLoading(true);

    const payload = {
      yearofexp: formData.yearOfExp,
      companyname: formData.ibName,
      existclients: formData.existingClients,
      avgvolume: formData.avgVolume,
    };

    try {
      const res = await axios.post(`${BASE_URL}/ib/apply_ib`, payload, {
        headers: getApiHeaders(),
      });

      if (res.data.code === 200) {
        MessageToster("success", res.data.message);
        closeModal();
        return true;
      } else {
        MessageToster("error", res.data.message || "Something went wrong");
        return false;
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const success = await createIBAccount();

    if (success) {
      await refreshIbStatus();
    }
  };

  return (
    <>
      <div id="layout-wrapper">
        {isPageLoading && <Loader />}
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <>
                {isPending && (
                  <div className="row justify-content-center">
                    <div
                      className="alert alert-warning alert-dismissible fade show"
                      role="alert"
                      style={{
                        marginBottom: "1.5rem",
                      }}
                    >
                      <i className="mdi mdi-timer-sand-full me-2" />
                      Your IB Account application status is pending. Please
                      allow up to 24 hours for verification and approval.
                    </div>

                    <div className="col-xl-5 col-lg-6 col-md-8 col-12">
                      <div className="card">
                        <div className="card-body text-center">
                          <img
                            src="assets/images/ib/overview.webp"
                            alt=""
                            width="280px"
                          />
                          <p className="fw-bold text-warning">
                            IB Application Submitted
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            disabled
                          >
                            Application Pending Review
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isActive && (
                  <>
                    <div className="row">
                      <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                          <h2 className="fw-700 mb-sm-0">
                            Client Accounts
                          </h2>
                        </div>
                      </div>
                    </div>
                    {/* table */}
                    <div className="row">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-xl-3 col-lg-6 col-sm-6">
                                <div className="card shadow-none bg-gray-subtle">
                                  <div className="card-body">
                                    <div className="text-center">
                                      <h5 className="fs-17"> Total Referral </h5>
                                      <h2 className="mb-0">
                                        {clientData?.total_referral !== undefined ? clientData.total_referral : 0}
                                      </h2>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-xl-3 col-lg-6 col-sm-6">
                                <div className="card shadow-none bg-pink-subtle">
                                  <div className="card-body">
                                    <div className="text-center">
                                      <h5 className="fs-17"> Total IB Accounts </h5>
                                      <h2 className="mb-0">
                                        {clientData?.total_ib_accounts !== undefined ? clientData.total_ib_accounts : 0}
                                      </h2>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-xl-3 col-lg-6 col-sm-6">
                                <div className="card shadow-none bg-orange-subtle">
                                  <div className="card-body">
                                    <div className="text-center">
                                      <h5 className="fs-17"> Total Created Accounts </h5>
                                      <h2 className="mb-0">
                                        {clientData?.total_client_created_accounts !== undefined ? clientData.total_client_created_accounts : 0}
                                      </h2>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-xl-3 col-lg-6 col-sm-6">
                                <div className="card shadow-none bg-sky-subtle">
                                  <div className="card-body">
                                    <div className="text-center">
                                      <h5 className="fs-17"> Total Client Accounts </h5>
                                      <h2 className="mb-0">
                                        {clientData?.total_client_accounts !== undefined ? clientData.total_client_accounts : 0}
                                      </h2>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
                              <div>
                                <label className="form-label mb-1">From Date</label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  value={fromDate}
                                  max={toDate || today}
                                  onChange={(e) => setFromDate(e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="form-label mb-1">To Date</label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  value={toDate}
                                  min={fromDate || undefined}
                                  max={today}
                                  onChange={(e) => setToDate(e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="form-label mb-1">Search</label>
                                <input
                                  type="search"
                                  className="form-control form-control-sm"
                                  placeholder="Search account, client ID..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                />
                              </div>
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary waves-effect"
                                  onClick={clearFilters}
                                  disabled={!fromDate && !toDate && !searchTerm}
                                >
                                  Clear
                                </button>
                              </div>
                            </div>

                            <CommonTable
                              columns={[
                                { header: "Date", render: (row) => row.create_date ? formatDate(row.create_date) : "-" },
                                { header: "Client account", accessor: "account_number" },
                                { header: "Client ID", accessor: "client_id" },
                                { header: "Platform", render: (row) => row.trading_platform || "-" },
                                { header: "Account type", render: (row) => row.account_type || "-" },
                                { header: "Currency", render: (row) => row.currency || "-" },
                                { header: "Status", render: (row) => row.account_status || "-" }
                              ]}
                              data={filteredClients}
                              loading={loading}
                              noDataMessage={hasFilters ? "No client accounts match your filters" : "No client accounts found"}
                            />
                          </div>
                        </div>
                      </div>{" "}
                      {/* end col */}
                      <div>
                        {/* modal */}
                        <div
                          className="modal fade bs-example-modal-center"
                          tabIndex={-1}
                          role="dialog"
                          aria-hidden="true"
                        >
                          <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                              <div className="modal-header">
                                <h5 className="modal-title">Transaction Details</h5>
                                <button
                                  type="button"
                                  className="btn-close"
                                  data-bs-dismiss="modal"
                                  aria-label="Close"
                                />
                              </div>
                              <div className="modal-body">
                                <table className="table mb-0 border">
                                  <tbody>
                                    <tr>
                                      <td>Transaction ID</td>
                                      <td className="fw-bold">TXjqx0006665</td>
                                    </tr>
                                    <tr>
                                      <td>From Account</td>
                                      <td className="fw-bold notranslate" translate="no">
                                        Tether USD (USDT TRC20)
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>To Account</td>
                                      <td className="fw-bold">91020</td>
                                    </tr>
                                    <tr>
                                      <td>Currency</td>
                                      <td className="fw-bold">USD</td>
                                    </tr>
                                    <tr>
                                      <td>Amount</td>
                                      <td className="fw-bold">100.00</td>
                                    </tr>
                                    <tr>
                                      <td>Commission(%)</td>
                                      <td className="fw-bold">0.0</td>
                                    </tr>
                                    <tr>
                                      <td>Commission Amount</td>
                                      <td className="fw-bold">0.0</td>
                                    </tr>
                                    <tr>
                                      <td>Net Amount</td>
                                      <td className="fw-bold">100.00</td>
                                    </tr>
                                    <tr>
                                      <td>Transaction Date</td>
                                      <td className="fw-bold">2024-08-20</td>
                                    </tr>
                                    <tr>
                                      <td>Modified Date</td>
                                      <td className="fw-bold">2024-08-20</td>
                                    </tr>
                                    <tr>
                                      <td>Status</td>
                                      <td className="fw-bold">Completed</td>
                                    </tr>
                                    <tr>
                                      <td>Reject</td>
                                      <td className="fw-bold">Payments</td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="card">
                                  <div className="mt-4">
                                    <div className="text-muted text-center">
                                      <div className="bg-primary-subtle p-3">
                                        <h4 className="card-title mb-0">
                                          Payment Details
                                        </h4>
                                      </div>
                                    </div>
                                    <div className="table-responsive mt-4">
                                      <table className="table align-middle mb-0">
                                        <tbody>
                                          <tr>
                                            <td>Transaction Hash</td>
                                            <td className="fw-bold">sdfsfsdfsdfd</td>
                                          </tr>
                                          <tr>
                                            <td>Deposit Receipt</td>
                                            <td className="fw-bold">
                                              {" "}
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-soft-success"
                                              >
                                                <i className="mdi mdi-eye-outline fs-5" />
                                              </button>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* /.modal-content */}
                          </div>
                          {/* /.modal-dialog */}
                        </div>
                        {/* /.modal */}
                      </div>
                    </div>
                  </>
                )}

                {isNotApplied && (
                  <div className="row justify-content-center">
                    <div className="col-xl-5 col-lg-6 col-md-8 col-12">
                      <div className="card">
                        <div className="card-body text-center">
                          <img
                            src="assets/images/ib/overview.webp"
                            alt=""
                            width="280px"
                          />
                          <p className="fw-bold">
                            Are you interested in becoming IB?
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light "
                            data-bs-toggle="modal"
                            data-bs-target=".bs-example-modal-center"
                            onClick={handleReset}
                          >
                            Join Us Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>

              {/* modal */}
              <div>
                <div
                  className="modal fade bs-example-modal-center"
                  tabIndex={-1}
                  role="dialog"
                  aria-hidden="true"
                  data-bs-backdrop="static"
                  data-bs-keyboard="false"
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">IB Account</h5>
                        <button
                          type="button"
                          className="btn-close"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        />
                      </div>
                      <div className="modal-body">
                        <div className="col-12 mb-3">
                          <label className="form-label">
                            IB Name <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="IB Name"
                            name="ibName"
                            value={formData.ibName}
                            onChange={handleChange}
                          />
                          {validationErrors.ibName && (
                            <small className="text-danger">{validationErrors.ibName}</small>
                          )}
                        </div>
                        <div className="col-12 mb-3">
                          <label className="form-label">
                            Year Of Experience
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="number"
                            name="yearOfExp"
                            placeholder="Year Of Experience"
                            value={formData.yearOfExp}
                            onChange={handleChange}
                          />
                          {validationErrors.yearOfExp && (
                            <small className="text-danger">{validationErrors.yearOfExp}</small>
                          )}
                        </div>
                        <div className="col-12 mb-3">
                          <label className="form-label">
                            No. Of Existing Client
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="number"
                            name="existingClients"
                            placeholder="No. Of Existing Client"
                            value={formData.existingClients}
                            onChange={handleChange}
                          />
                          {validationErrors.existingClients && (
                            <small className="text-danger">{validationErrors.existingClients}</small>
                          )}
                        </div>
                        <div className="col-12 mb-3">
                          <label className="form-label">
                            Average Volume Per Month
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="number"
                            name="avgVolume"
                            placeholder="Average Volume Per Month"
                            value={formData.avgVolume}
                            onChange={handleChange}
                          />
                          {validationErrors.avgVolume && (
                            <small className="text-danger">{validationErrors.avgVolume}</small>
                          )}
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setFormData({
                              firstName: "",
                              lastName: "",
                              yearsOfExperience: "",
                              companyName: "",
                              existingClients: "",
                              avgVolume: ""
                            });
                            setValidationErrors({});
                          }}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          {loading ? "Submitting..." : "Submit"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
